export enum SERVER_SERVER_PROTOCOLS {
    SERVER_TO_SERVER_MESSAGE,
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENT,
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CLIENTS,
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_AREA,
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_SERVER,
    SERVER_TO_SERVER_REQUEST,
    SERVER_TO_SERVER_RESPONSE,
    UNREGISTER_SERVER,
    // RESERVED (ordinal only): never handled — it made the receiver connect to
    // any address a peer named. See docs/security.md.
    PREPARE_FOR_SERVER_REGISTRATION,
    HEARTBEAT_PING,
    HEARTBEAT_PONG,
    REGISTER_SERVER,
    CONFIRM_REGISTRATION,
    IS_MULTIPLE_MESSAGES,
    MASTER_BROADCAST,
    // Relay variants that carry an already-serialized client payload as its OWN
    // frame (via publishRelay) instead of re-embedding it in the envelope array
    // — avoids the double JSON encode/escape the SERVER_TO_SERVER_MESSAGE path
    // pays. CHANNEL carries the channelIndex in the prefix; RAW_TO_AREA
    // broadcasts a base64 raw payload to every client the connector holds for
    // the area. Append-only — ordinal is the wire value.
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_TO_CHANNEL,
    SERVER_TO_SERVER_MESSAGE_FAST_RELAY_RAW_TO_AREA
}

export enum SERVER_TYPES {
    CONNECTOR,
    AREA,
    GATE,
    MASTER,
    // A process that can spawn/destroy dynamic area instances on request
    // (append-only — ordinal is the wire value).
    HOST
}

export enum LEAVE_CHANNEL_REASONS {
    DISCONNECTED = 'disconnected',
    REMOVED = 'removed',
    CHANNEL_DESTROYED = 'channel_destroyed',
    REMOVED_BY_AREA = 'removed_by_area',
    SWITCHED_AREA = 'switched_area',
}

// Union type for API consumers that may pass either a well-known enum value
// or an arbitrary string reason (e.g. 'kicked'). Use this in public signatures
// so the allowed shape is discoverable and documented.
export type LeaveChannelReason = LEAVE_CHANNEL_REASONS | string;

export enum MASTER_AREA_PROTOCOLS {
    AREA_MESSAGE,
    AREA_REQUEST,
    // RESERVED (ordinal only): never handled — see SERVER_SERVER_PROTOCOLS.PREPARE_FOR_SERVER_REGISTRATION.
    PREPARE_FOR_CONNECTOR,
    RELAY_AREA_REQUEST_OR_MESSAGE,
    SYNC_GAME_AREA_DATA,
    SYNC_GLOBAL_AREA_DATA,

    //todo: rework $gameData and $areaData to only be changable on master and make current gameData and add a $gameState which is also global across areas, and also an $areaState which is only on areas
    SET_GLOBAL_GAME_STATE,
    SET_GLOBAL_GAME_DATA,
    SET_GLOBAL_AREA_DATA,
    UPDATE_GLOBAL_GAME_DATA,
    UPDATE_GLOBAL_AREA_DATA,

    // Dynamic area instances (append-only — ordinal is the wire value).
    SYNC_DYNAMIC_AREA_DATA,
    SPAWN_AREA,
    AREA_READY,
    TEARDOWN_AREA,
    AREA_CLOSED,
    HOST_REPORT_AREAS,
    AREA_MEMBERSHIP,
    // Host agent asks the master to prepare itself + the connectors for an
    // instance area's registration handshake; reply carries their configs.
    AREA_PREPARE,
    // Author system API: an area asks the master to create / destroy a dynamic
    // area (`system.createArea` / `system.destroyArea`). The requesting area is
    // recorded as the owner (`createdBy`) — the only non-master allowed to destroy it.
    CREATE_AREA,
    DESTROY_AREA,

    // Unified Assets API — cluster propagation (append-only; ordinal = wire value).
    // Area→master write request (request/response) and cold read; master→area change fan-out.
    ASSET_WRITE,
    ASSET_READ,
    ASSET_CHANGED,

    // Connector failover: master→area notifies of connector changes
    CONNECTOR_CHANGE
}

export enum MASTER_CONNECTOR_PROTOCOLS {
    // Master asks a connector to prepare for / tear down a dynamic instance
    // area's registration (remote spawn choreography).
    PREPARE_INSTANCE_AREA,
    REMOVE_INSTANCE_AREA,
    // Master→connector: a client-namespace asset changed; connector broadcasts it to clients.
    ASSET_CHANGED
}

export enum MASTER_GATE_PROTOCOLS {
    ADD_GAME,
    REMOVE_GAME,
    UPDATE_GAMES,
    PREPARE_FOR_CONNECTOR,
    UPDATE_GAME_META_DATA,

    // Dynamic area instances — gate-side matchmaking relay (append-only).
    CREATE_DYNAMIC_AREA,
    DESTROY_DYNAMIC_AREA,
    GET_AREA_CAPACITY,
    LIST_DYNAMIC_AREAS,

    // Connector failover: master→gate notifies of connector changes
    CONNECTOR_CHANGE,
}